import { getAnnualDiscountPercent } from '@/components/PricingPage/PricingCards/getAnnualDiscountPercent';
import { usePricingOptions } from '@/hooks/api/usePricingOptions';
import { useCurrency } from '@/hooks/billing/useCurrency';
import { useIsClient } from '@/hooks/ui/useIsClient';
import { getFallbackPrice } from '@/data/pricing-fallbacks';
import { getDecimalSeparator } from '@/utils/getDecimalSeparator';

import type { PricingCardProps } from '@/components/PricingPage/PricingCards/PricingCard';
import type { PricingByCurrency } from '@/hooks/api/usePricingOptions';
import type { FallbackPlanKey } from '@/data/pricing-fallbacks';
import type { Settings } from '@/types/settings';

const MONTHS_PER_YEAR = 12;
const PERCENT = 100;

/**
 * Marketing-approved display percentages. A raw computed discount inside
 * a band snaps to its `display` value; anything outside falls through to
 * Math.floor. The gaps between bands (e.g. 17–19, 27–28, 37–38, 44–47)
 * are intentional: when pricing lands in a gap we'd rather show the
 * truthful floored value than snap to a tidy number that misrepresents
 * the actual saving.
 */
const DISCOUNT_BANDS: ReadonlyArray<{
  min: number;
  max: number;
  display: number;
}> = [
  { min: 14, max: 16, display: 15 },
  { min: 20, max: 23.5, display: 20 },
  { min: 24, max: 26, display: 25 },
  { min: 29, max: 31, display: 30 },
  { min: 33, max: 34, display: 33 },
  { min: 35, max: 36, display: 35 },
  { min: 39, max: 43, display: 40 },
  { min: 48, max: 52, display: 50 },
];

const snapToBand = (rawPercent: number): number => {
  const safePercent = Math.max(0, rawPercent);
  const band = DISCOUNT_BANDS.find(
    (b) => safePercent >= b.min && safePercent <= b.max
  );
  return band ? band.display : Math.floor(safePercent);
};

/**
 * @param serverCurrency Currency resolved from the request. `useCurrency`
 * reads SettingsContext, which is empty during SSR — `_app` seeds it from
 * `localStorage`, and that throws on the server — so without this the server
 * prices the discount in USD while the client uses the visitor's real
 * currency. Wherever the two land in different bands (GBP, EUR, CAD) that's a
 * hydration mismatch, and React 19 answers it by re-rendering the page.
 * Mirrors what PricingCard already does for the prices themselves (DEV-6494).
 */
const usePlanPricing = (
  planType: PricingCardProps['type'],
  serverCurrency?: Settings['currency']
) => {
  const { data: pricingOptions } = usePricingOptions();
  const clientCurrency = useCurrency();
  const isMounted = useIsClient();

  // Only defer for callers that supply one. Passing none keeps the old
  // client-currency-immediately behaviour, which is what the account and
  // checkout surfaces want — they mount after hydration, so deferring there
  // would just flash the USD figure at them.
  const currency =
    serverCurrency && !isMounted ? serverCurrency : clientCurrency;

  const currencyCode = (currency?.code?.toLowerCase() ??
    'usd') as keyof PricingByCurrency;
  const option = pricingOptions?.find(
    (o) => o.title.toLowerCase() === planType.toLowerCase()
  );

  return { currency, currencyCode, pricing: option?.pricing[currencyCode] };
};

export const useAnnualDiscountPercent = (
  planType: PricingCardProps['type'],
  serverCurrency?: Settings['currency']
): number => {
  const { pricing } = usePlanPricing(planType, serverCurrency);
  const fallback = getAnnualDiscountPercent({ planType });

  if (planType === 'Free') {
    return fallback;
  }

  if (!(pricing?.monthly && pricing?.yearly)) {
    return fallback;
  }

  const rawPercent =
    (1 - pricing.yearly / (pricing.monthly * MONTHS_PER_YEAR)) * PERCENT;

  return snapToBand(rawPercent);
};

/**
 * The plan's monthly-billing price, formatted with the currency symbol, e.g.
 * "£8.99". Same currency resolution as useAnnualDiscountPercent.
 */
export const useMonthlyPriceLabel = (
  planType: Exclude<PricingCardProps['type'], 'Free'>,
  serverCurrency?: Settings['currency']
): string => {
  const { currency, currencyCode, pricing } = usePlanPricing(
    planType,
    serverCurrency
  );
  const monthly =
    pricing?.monthly ??
    getFallbackPrice(
      planType.toLowerCase() as FallbackPlanKey,
      currencyCode,
      'monthly'
    );
  const [integer, decimals] = String(monthly).split('.');
  const separator = getDecimalSeparator(currency?.code ?? 'USD');

  return `${currency?.symbol_left ?? '$'}${integer}${decimals ? `${separator}${decimals.padEnd(2, '0')}` : ''}`;
};
